import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectSite } from '../../features/site/siteSlice';

/**
 * Regional configuration derived from the site's IANA timezone.
 *
 * This provides locale-aware defaults (week start day, hour format, etc.)
 * so the app behaves correctly regardless of where the site is located.
 */
export type RegionalConfig = {
  /** 0 = Sunday, 1 = Monday. Used by date-fns and Intl APIs. */
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** Whether the region uses 12-hour (AM/PM) time by default. */
  hour12: boolean;
};

/**
 * Maps IANA timezone prefixes to regional settings.
 *
 * Longest prefix wins (sorted at lookup time), so specific entries like
 * 'America/Argentina/' override the generic 'America/' fallback.
 *
 * This is intentionally coarse-grained. When more precision is needed,
 * expand with country-level overrides.
 */
const TIMEZONE_REGION_MAP: Record<string, RegionalConfig> = {
  // North America (US/Canada/Mexico) — Sunday start, 12-hour.
  // This is the fallback for any America/ zone not matched by a more specific entry below.
  'America/': { weekStartsOn: 0, hour12: true },

  // South America — Monday start, 24-hour
  'America/Argentina/': { weekStartsOn: 1, hour12: false },
  'America/Sao_Paulo': { weekStartsOn: 1, hour12: false },
  'America/Fortaleza': { weekStartsOn: 1, hour12: false },
  'America/Recife': { weekStartsOn: 1, hour12: false },
  'America/Bahia': { weekStartsOn: 1, hour12: false },
  'America/Manaus': { weekStartsOn: 1, hour12: false },
  'America/Belem': { weekStartsOn: 1, hour12: false },
  'America/Cuiaba': { weekStartsOn: 1, hour12: false },
  'America/Campo_Grande': { weekStartsOn: 1, hour12: false },
  'America/Porto_Velho': { weekStartsOn: 1, hour12: false },
  'America/Rio_Branco': { weekStartsOn: 1, hour12: false },
  'America/Bogota': { weekStartsOn: 1, hour12: false },
  'America/Lima': { weekStartsOn: 1, hour12: false },
  'America/Santiago': { weekStartsOn: 1, hour12: false },
  'America/Caracas': { weekStartsOn: 1, hour12: false },
  'America/Guayaquil': { weekStartsOn: 1, hour12: false },
  'America/Asuncion': { weekStartsOn: 1, hour12: false },
  'America/Montevideo': { weekStartsOn: 1, hour12: false },
  'America/La_Paz': { weekStartsOn: 1, hour12: false },

  // Europe — Monday start, 24-hour default
  'Europe/': { weekStartsOn: 1, hour12: false },
  // Asia — varies, default Monday start
  'Asia/': { weekStartsOn: 1, hour12: false },
  // Oceania (Australia/NZ) — Monday start, 12-hour
  'Australia/': { weekStartsOn: 1, hour12: true },
  'Pacific/Auckland': { weekStartsOn: 1, hour12: true },
  'Pacific/': { weekStartsOn: 0, hour12: true },
  // Africa — Monday start, 24-hour
  'Africa/': { weekStartsOn: 1, hour12: false },
};

/** Default config when timezone is empty or unrecognized — US-centric fallback. */
const DEFAULT_CONFIG: RegionalConfig = {
  weekStartsOn: 0,
  hour12: true,
};

/**
 * Resolves a RegionalConfig from an IANA timezone string.
 * Pure function — can be called outside React components.
 */
export function getRegionalConfig(timezone: string): RegionalConfig {
  if (!timezone) return DEFAULT_CONFIG;

  // Try longest prefix match first (e.g. 'Pacific/Auckland' before 'Pacific/')
  const sortedKeys = Object.keys(TIMEZONE_REGION_MAP).sort((a, b) => b.length - a.length);
  for (const prefix of sortedKeys) {
    if (timezone.startsWith(prefix)) {
      return TIMEZONE_REGION_MAP[prefix];
    }
  }

  return DEFAULT_CONFIG;
}

/**
 * React hook that returns the regional configuration for the current site.
 * Derives settings from `site.timezone` in Redux state.
 *
 * Usage:
 * ```ts
 * const { weekStartsOn, hour12 } = useRegionalConfig();
 * ```
 */
export function useRegionalConfig(): RegionalConfig {
  const { timezone } = useSelector(selectSite);
  return useMemo(() => getRegionalConfig(timezone), [timezone]);
}
